import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, NgZone, OnDestroy, inject } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, Subject, interval } from "rxjs";
import { share, takeUntil } from "rxjs/operators";
import { SkipCustomInterceptor } from "~app/common/custom-interceptor";
import { IContent } from "~app/common/models/validationModel";
import { environment } from "../../../environments/environment";
import { RaskModalService } from "../components/modal/rask-modal.service";
import { DateUtils } from "../utils/date-utils";

@Injectable({
    providedIn: "root"
})
export class RaskVersionChecker implements OnDestroy {
    readonly diffUntilVersionCheck = 1;
    readonly statusCodeRaskVersionDiff = 410;

    hostName: string;
    isAuthenticated: boolean;
    lastRequest: Date;

    private readonly http = inject(HttpClient);
    private readonly modalService = inject(RaskModalService);
    private readonly router = inject(Router);
    private readonly unsubscribe$: Subject<void> = new Subject<void>();
    private readonly zone = inject(NgZone);

    constructor() {
        this.hostName = window.location.hostname;
        this.startApiCheckTimer();
    }

    apiGet<T>(route: string, params: any = null): Observable<T> {
        const url = `${environment.apiProxy}/${route}`;
        const headers = new HttpHeaders().set(SkipCustomInterceptor, "-1");
        return this.http.get<T>(url, { headers: headers,
            params: params }).pipe(share());
    }

    authenticated(isAuthenticated: boolean) {
        this.isAuthenticated = isAuthenticated;
    }

    gotoMaintenancePage() {
        this.router.navigate(["maintenance"]);
    }

    ngOnDestroy() {
        this.stopApiCheckTimer();
    }

    requestExecuted(uri: string) {
        if (this.isAuthenticated && uri.includes(this.hostName)) {
            this.lastRequest = DateUtils.now();
        }
    }

    showModalMaintenanceError(hasOverrideHeader: boolean) {
        if (!this.modalService.isDialogOpen()) {
            if (hasOverrideHeader) {
                this.gotoMaintenancePage();
                return;
            }

            const msg = {
                message: "RASK är för närvarande inte tillgängligt.",
                messageType: "UserMessage"
            } as IContent;

            this.zone.run(() => {
                this.modalService.error(
                    "Information",
                    msg,
                    "static").subscribe();
            });
        }
    }

    showModalVersionError() {
        if (!this.modalService.isDialogOpen()) {
            const msg = {
                message: `Rask har uppdaterats till en ny version och sidan kommer att laddas om när du stänger denna informationsruta.
                 Eventuella osparade ändringar kommer inte att sparas.`,
                messageType: "UserMessage"
            } as IContent;

            this.zone.run(() => {
                this.modalService.error(
                    "Information",
                    msg,
                    "static").subscribe(x => {
                    window.location.reload();
                });
            });
        }
    }

    startApiCheckTimer() {
        this.lastRequest = DateUtils.now();
        this.zone.runOutsideAngular(() => {
            interval(30000)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(() => {
                    if (this.isAuthenticated) {
                        const diff = DateUtils.minutesBetween(this.lastRequest, DateUtils.now());
                        if (diff >= this.diffUntilVersionCheck) {
                            // eslint-disable-next-line sonarjs/deprecation
                            this.apiGet<boolean>("authentication/active").subscribe(() => {
                                this.lastRequest = DateUtils.now();
                            }, error => {
                                if (error.status === this.statusCodeRaskVersionDiff) {
                                    this.showModalVersionError();
                                }
                            });
                        }
                    }
                });
        });
    }

    stopApiCheckTimer() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
